import { useEffect, useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { useAuthStore } from '../../store/authStore.js'

export interface MfaResetDialogProps {
  open: boolean
  /** Who is being reset, for the message. */
  targetName: string
  onClose: () => void
  /** Called with the acting admin's current password; throw to show an error. */
  onSubmit: (password: string, loginId: string | undefined) => Promise<void>
}

/** Step-up confirmation for resetting someone else's MFA. */
export function MfaResetDialog({ open, targetName, onClose, onSubmit }: MfaResetDialogProps): JSX.Element {
  const me = useAuthStore((st) => st.user)
  const loginId = me?.loginId || me?.email || undefined
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) {
      setPassword('')
      setError(null)
      setSubmitting(false)
    }
  }, [open])

  async function submit(): Promise<void> {
    setSubmitting(true)
    setError(null)
    try {
      await onSubmit(password, loginId)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reset MFA')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog header="Reset MFA" visible={open} onHide={onClose} style={{ width: '28rem' }} modal>
      <p className="mb-3 text-sm text-gray-600">
        Reset MFA for <strong>{targetName}</strong>? They will be signed out of all devices, emailed, and asked to set MFA up again.
      </p>
      {loginId && (
        <p className="mb-2 text-xs text-gray-500">Signed in as <strong>{loginId}</strong></p>
      )}
      <label htmlFor="mfa-reset-password" className="mb-1 block text-sm font-medium text-gray-700">
        Confirm with your password
      </label>
      <input
        id="mfa-reset-password"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
      />
      {error && <p role="alert" className="mt-2 text-sm text-red-600">{error}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <Button label="Cancel" severity="secondary" outlined onClick={onClose} disabled={submitting} />
        <Button label="Reset MFA" severity="danger" onClick={() => void submit()} disabled={submitting || !password} loading={submitting} />
      </div>
    </Dialog>
  )
}
